/**
 * Plush: slider navigation inside the product zoom modal.
 *
 * Dawn stacks every image in a scroll container with no way to step between them.
 * This adds prev/next buttons, a live counter, and arrow-key support that scroll
 * the modal to the previous/next image.
 *
 * Everything is queried LIVE from the DOM on each interaction — Dawn re-renders
 * the modal content on open and on variant change, so caching element/slide
 * references leads to stale nodes (and NaN counters). Slide count is derived from
 * the real scrollWidth, so hidden/absent media never throw the math off.
 */
(function () {
  'use strict';

  function contentEl(modal) {
    return modal.querySelector('.product-media-modal__content');
  }

  function step(modal) {
    var c = contentEl(modal);
    return (c && c.clientWidth) || 1;
  }

  // Number of full-width "pages" actually laid out (works whether some media are
  // hidden or not — it's based on real scrollable width).
  function pageCount(modal) {
    var c = contentEl(modal);
    if (!c) return 1;
    return Math.max(1, Math.round(c.scrollWidth / step(modal)));
  }

  function indexFromScroll(modal) {
    var c = contentEl(modal);
    if (!c) return 0;
    var i = Math.round(c.scrollLeft / step(modal));
    if (!isFinite(i)) i = 0;
    return Math.max(0, Math.min(pageCount(modal) - 1, i));
  }

  function paint(modal, i) {
    var current = modal.querySelector('[data-plush-modal-current]');
    var total = modal.querySelector('[data-plush-modal-total]');
    var prev = modal.querySelector('[data-plush-modal-prev]');
    var next = modal.querySelector('[data-plush-modal-next]');
    var pages = pageCount(modal);
    var last = pages - 1;
    if (current) current.textContent = (i + 1);
    // Total reflects the actually-shown images (variant filtering hides the rest),
    // so a 5-image colour reads "n / 5", not "n / 15".
    if (total) total.textContent = pages;
    if (prev) prev.disabled = i <= 0;
    if (next) next.disabled = i >= last;

    // Dots: one per real page. Variant filtering can show fewer images than the
    // product has media, so extras are hidden rather than left dangling.
    var dots = modal.querySelectorAll('[data-plush-modal-dot]');
    for (var d = 0; d < dots.length; d++) {
      dots[d].hidden = d >= pages;
      dots[d].classList.toggle('is-active', d === i);
    }
  }

  function goTo(modal, index) {
    var c = contentEl(modal);
    if (!c) return;
    var i = Math.max(0, Math.min(pageCount(modal) - 1, index));
    if (!isFinite(i)) i = 0;
    // `scroll-snap-type: x mandatory` fights a smooth programmatic scroll on
    // mobile and snaps back to the current slide. Disable snap for the scroll,
    // then restore it.
    var prevSnap = c.style.scrollSnapType;
    c.style.scrollSnapType = 'none';
    c.scrollTo({ left: i * step(modal), behavior: 'smooth' });
    window.clearTimeout(c.__plushSnapTimer);
    c.__plushSnapTimer = window.setTimeout(function () {
      c.style.scrollSnapType = prevSnap;
    }, 500);
    paint(modal, i);
  }

  function setup(modal) {
    if (modal.dataset.plushModalNav === '1') return;
    var content = contentEl(modal);
    var prev = modal.querySelector('[data-plush-modal-prev]');
    var next = modal.querySelector('[data-plush-modal-next]');
    if (!content || !prev || !next) return;
    modal.dataset.plushModalNav = '1';

    // Dawn's media-modal closes on any pointer/mouse up that isn't inside
    // deferred-media/product-model — that fires before `click`, so the pointer
    // events must be stopped too or the modal shuts when an arrow is tapped.
    function swallow(event) {
      event.stopPropagation();
    }
    [prev, next].forEach(function (button) {
      ['pointerdown', 'pointerup', 'mouseup', 'touchstart', 'touchend'].forEach(function (ev) {
        button.addEventListener(ev, swallow, { passive: true });
      });
    });

    prev.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      goTo(modal, indexFromScroll(modal) - 1);
    });
    next.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      goTo(modal, indexFromScroll(modal) + 1);
    });

    modal.querySelectorAll('[data-plush-modal-dot]').forEach(function (dot) {
      ['pointerdown', 'pointerup', 'mouseup', 'touchstart', 'touchend'].forEach(function (ev) {
        dot.addEventListener(ev, swallow, { passive: true });
      });
      dot.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        goTo(modal, parseInt(dot.getAttribute('data-plush-modal-dot'), 10) || 0);
      });
    });

    var ticking = false;
    content.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          paint(modal, indexFromScroll(modal));
          ticking = false;
        });
      },
      { passive: true }
    );

    modal.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        event.preventDefault();
        goTo(modal, indexFromScroll(modal) + 1);
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        event.preventDefault();
        goTo(modal, indexFromScroll(modal) - 1);
      }
    });

    // Open on the photo that was clicked. Dawn's showActiveMedia() marks it
    // .active and scrollIntoView()s it, then sets scrollLeft for its original
    // *vertical* modal, which in this horizontal slider snaps back to photo 1.
    // Once the modal is open, jump straight to the active photo instead.
    new MutationObserver(function () {
      if (!modal.hasAttribute('open')) return;
      window.setTimeout(function () {
        var c = contentEl(modal);
        var active = c && c.querySelector('.active');
        if (!c || !active || active.hidden) return;
        var i = Math.round(
          (active.getBoundingClientRect().left - c.getBoundingClientRect().left + c.scrollLeft) / step(modal)
        );
        if (!isFinite(i) || i < 0) i = 0;
        var prevSnap = c.style.scrollSnapType;
        c.style.scrollSnapType = 'none';
        c.scrollLeft = i * step(modal);
        c.style.scrollSnapType = prevSnap;
        paint(modal, indexFromScroll(modal));
      }, 30);
    }).observe(modal, { attributes: true, attributeFilter: ['open'] });

    paint(modal, indexFromScroll(modal));
  }

  function init() {
    document.querySelectorAll('product-modal').forEach(setup);
  }

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }

  // The modal is populated/re-rendered on open and on variant change; re-paint
  // the counter shortly after any click so it reflects the freshly-shown media.
  document.addEventListener('click', function () {
    window.setTimeout(function () {
      init();
      document.querySelectorAll('product-modal[open]').forEach(function (modal) {
        paint(modal, indexFromScroll(modal));
      });
    }, 60);
  });
})();
